import React, { useEffect, useRef, useState } from 'react';
import t from 'react-translate';
import { css } from '@emotion/react';
import { Button } from 'react-bootstrap';

import { doubleSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { ReactComponent as ColorSwapActiveIcon } from 'styles/icons/color-swap.svg';
import { ReactComponent as ColorSwapDisableIcon } from 'styles/icons/color-swap-disabled.svg';
import { black, hexToRgbaString, primary, warning } from 'styles/global_defaults/colors';

import { useAppDispatch } from 'redux/store';
import { useSelector } from 'react-redux';
import { getCurrentLecture } from 'redux/selectors/lecture-page';
import { getCurrentCourse, getFlatCourseAliases } from 'redux/selectors/course';
import { setUpdatingColors } from 'redux/actions/lecture-pages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { addAlertMessage, removeAlertMessage } from 'redux/actions/alert-messages';

import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';

import pusherService from 'shared/services/pusher-service';
import NvTooltip from 'shared/components/nv-tooltip';
import ClickableContainer from 'components/clickable-container';
import { config } from '../../../../config/config.json';
import TopPanelActionButton from '../edit-menu/actions/top-panel-action-button';
import UpdateColorModal from './update-color-modal';

export const UpdateColor = () => {
  const styles = css`
    .update-color-icon-active {
      border: 2px solid ${warning};
    }

    .bs4-dropdown {
      .condensed-dropdown {
        margin: auto;
        font-size: 16px;
        line-height: 25px;
        font-weight: 700 !important;
        color: ${primary};
      }
    }

    .bs4-dropdown-menu {
      padding-top: 10px !important;
      padding-bottom: 10px !important;
      max-width: 460px;
    }

    .dropdown-item-ellipsis  strong {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      max-width: 280px;
      display: inline-block;
      vertical-align: bottom;
    }
  `;

  const dispatch = useAppDispatch();
  const iconRef = useRef<HTMLDivElement | null>(null);
  const [showColorModal, setShowColorModal] = useState(false);
  const [findColor, setFindColor] = useState('');
  const [replaceColor, setReplaceColor] = useState('');

  const params = useLecturePageParams();
  const currentLecture = useSelector(getCurrentLecture);
  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const { isUpdating } = useSelector((state) => state.app.lecturePage.updateColors);
  const updateColorbuttonIsEnabled = !isUpdating && !currentLecture?.isLinked;
  const { id: courseId } = useSelector(getCurrentCourse);

  // Effect: Setup pusher only when modal opens
  useEffect(() => {
    const pusherChannel = pusherService.initializeCourseChannel(courseId);
    pusherChannel.bind('replaced_course_color', (result) => {
      dispatch(setUpdatingColors(false));
      dispatch(removeAlertMessage());

      const colorToReplace = result?.color_to_replace;
      const newColor = result?.new_color;
      if (result?.success) {
        dispatch(addAlertMessage({
          type: AlertMessageType.SUCCESS,
          header: t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.ALERT_MESSAGES.ALERT_SUCCESS_HEADER(),
          message: t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.ALERT_MESSAGES.ALERT_SUCCESS_MSG(colorToReplace, newColor),
          button: <Button variant='primary' onClick={() => window.location.reload()}> {t.TIMELINE.RELOAD()} </Button>,
        }));
      } else {
        dispatch(addAlertMessage({
          type: AlertMessageType.INFO,
          header: t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.ALERT_MESSAGES.ALERT_ERROR_HEADER(),
          message: t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.ALERT_MESSAGES.ALERT_ERROR_MSG(colorToReplace),
        }));
      }
    });

    return () => {
      pusherChannel.unbind('replaced_course_color');
    };
  }, [courseId, dispatch]);

  const toggleUpdateColorModal = () => {
    setShowColorModal(prev => !prev);
  };

  const disabledUpdateColorTooltipText = isUpdating
    ? t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.TOOLTIP_TEXT.TOOLTIP_TEXT_UPDATING_IN_PROGRESS()
    : t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.TOOLTIP_TEXT.TOOLTIP_TEXT_FOR_LINKED_LESSON(courseAliases);

  return (
    <div css={styles}>
      <TopPanelActionButton
        ref={iconRef}
        tooltipText={
          updateColorbuttonIsEnabled
            ? t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.UPDATE_COLORS()
            : disabledUpdateColorTooltipText
        }
        className={`${showColorModal ? 'update-color-icon-active' : ''}`}
        onClick={toggleUpdateColorModal}
        dataQa={config.pendo.updateColors.updateColorsColorSwap}
        ariaLabel={t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.UPDATE_COLORS()}
        disabled={!updateColorbuttonIsEnabled}
        customIcon={
          updateColorbuttonIsEnabled
            ? <ColorSwapActiveIcon height={standardSpacing} width={standardSpacing} />
            : <ColorSwapDisableIcon height={standardSpacing} width={standardSpacing} />
        }
      />

      {showColorModal && (
        <UpdateColorModal
          onClose={() => setShowColorModal(false)}
          iconRef={iconRef}
          findColor={findColor}
          setFindColor={setFindColor}
          replaceColor={replaceColor}
          setReplaceColor={setReplaceColor}
        />
      )}
    </div>
  );
};

export default UpdateColor;
